import type { ReactNode } from 'react'
import { Modal, ScrollArea } from '@mantine/core'
import { CountChip } from './CrmUi'

/**
 * The CRM's one dialog shape (PD-1993): centred, a header with an optional icon tile, the
 * title, and a line of badges and subtitle; the content in sections; the actions in a footer
 * that stays in view.
 */
export function CrmModal({
  opened,
  onClose,
  title,
  icon,
  subtitle,
  badges,
  footer,
  size = 'lg',
  closeOnEscape = true,
  centered = true,
  children,
}: {
  opened: boolean
  onClose: () => void
  title: string
  icon?: ReactNode
  subtitle?: ReactNode
  badges?: ReactNode
  footer?: ReactNode
  size?: string | number
  /** Off while a dialog opened over this one is up, so Escape closes only that dialog. */
  closeOnEscape?: boolean
  /** Off for a dialog whose content changes height as you type, so it doesn't jump. */
  centered?: boolean
  children: ReactNode
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        icon ? (
          <span className="flex items-center gap-3">
            {icon}
            {title}
          </span>
        ) : (
          title
        )
      }
      size={size}
      closeOnEscape={closeOnEscape}
      centered={centered}
      radius={18}
      padding={0}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
      styles={{
        content: {
          boxShadow: '0 1px 3px rgba(16,24,40,.05), 0 24px 60px rgba(16,24,40,.2)',
        },
        header: { padding: '24px 24px 12px' },
        title: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: '#1F2733' },
        close: { border: '1px solid #E4E7EB', borderRadius: 8, color: '#6B7280' },
        body: { padding: 0 },
      }}
    >
      {(subtitle || badges) && (
        // Under the title, past the icon tile when there is one.
        <div className={`flex flex-wrap items-center gap-2 px-6 pb-5 ${icon ? 'pl-[76px]' : ''}`}>
          {badges}
          {subtitle && <span className="text-sm font-medium text-[#6B7280]">{subtitle}</span>}
        </div>
      )}
      {/* Takes the opening focus, so the close button isn't outlined on open. */}
      <div data-autofocus tabIndex={-1} className="flex flex-col gap-5 px-6 pb-5 outline-none">
        {children}
      </div>
      {footer && (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2.5 border-t border-[#EAECEF] bg-white px-6 py-4">
          {footer}
        </div>
      )}
    </Modal>
  )
}

/** A titled panel inside a CRM dialog. */
export function CrmSection({
  title,
  count,
  children,
}: {
  title: string
  count?: number
  children: ReactNode
}) {
  return (
    <section className="flex min-w-0 flex-col gap-4 rounded-[14px] border border-[#E7E9EC] p-5">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold text-[#1F2733]">{title}</h3>
        {count !== undefined && <CountChip>{count}</CountChip>}
      </div>
      {children}
    </section>
  )
}
